import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Globe, Award, Factory, Users } from "lucide-react";
import { PublicLayout } from "../components/Layout";
import HeroSlideshow from "../components/HeroSlideshow";
import Timeline from "../components/Timeline";
import AnimatedCounter from "../components/AnimatedCounter";


const Home = () => {
  return (
    <PublicLayout>
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-screen bg-[#222629] overflow-hidden">
        <div className="grain absolute inset-0" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(46,79,174,0.35),_transparent_60%)]" />

        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-10 pt-32 md:pt-40 pb-20 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center min-h-screen w-full min-w-0">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="min-w-0 w-full"
          >
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#B8823E] mb-6">
              <span className="w-8 h-px bg-[#B8823E]" /> Est. 1955 · Since 70 Years
            </div>
            <h1 className="font-display font-semibold text-white text-[2.15rem] leading-[1.06] sm:text-5xl lg:text-[3.75rem] xl:text-[4rem] tracking-tight">
              Materials That Power Industries.<br />
              <span className="text-[#B8823E]">Partnerships That Build Tomorrow.</span>
            </h1>
            <p className="mt-6 md:mt-8 text-sm md:text-base text-neutral-300 max-w-xl font-body leading-relaxed">
              Every industry builds something valuable. But every production process also leaves behind materials with untapped potential. At <strong className="text-white">R K STEEL</strong>, we believe those materials deserve a second life — not a landfill. For over 70 years, we have partnered with industries to responsibly source, recycle, and trade steel scrap, used stainless steel, aluminium scrap, copper, brass, stock-lot conveyor belts, and industrial rubber materials. By transforming industrial by-products into valuable resources, we help businesses operate more efficiently while contributing to a cleaner, more sustainable world across more than 30 countries.
            </p>
            <div className="mt-4 text-[11px] tracking-[0.24em] uppercase text-[#B8823E]/70">Recycling Today · Building Tomorrow</div>
            <div className="mt-7 md:mt-8 flex flex-wrap gap-3 md:gap-4">
              <Link to="/products" data-testid="hero-cta-products" className="btn-pill btn-primary">
                Explore Products <ArrowRight size={16} />
              </Link>
              <Link to="/contact" data-testid="hero-cta-contact" className="btn-pill" style={{ background: "transparent", color: "#B8823E", border: "1px solid rgba(242,214,165,0.3)" }}>
                Talk to us
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full min-w-0"
          >
            <div className="relative rounded-2xl overflow-hidden w-full">
              <HeroSlideshow height={560} />
            </div>
            <div className="mt-6 mx-auto max-w-md bg-white/5 backdrop-blur-xl border border-white/10 rounded-full px-5 py-2.5 text-xs text-neutral-300 flex items-center justify-between">
              <span className="tracking-wide">Live global operations</span>
              <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#2F9E44] animate-pulse" /> 11 hubs active</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* COUNTERS */}
      <section data-testid="counters-section" className="bg-[#F7F8F5] py-16 md:py-28 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            {[
              { label: "Years of Legacy", to: 70, suffix: "", accent: "#12355B", testId: "counter-years" },
              { label: "Countries Served", to: 30, suffix: "+", accent: "#2F9E44", testId: "counter-countries" },
              { label: "Tonnes Recycled", to: 2.5, suffix: "M+", decimals: 1, accent: "#B8823E", testId: "counter-tonnes" },
              { label: "Global Clients", to: 100, suffix: "+", accent: "#2F9E44", testId: "counter-clients" },
            ].map(c => (
              <motion.div
                key={c.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -4 }}
                className="lift-card bg-white rounded-2xl p-5 sm:p-6 md:p-7 border border-neutral-200/70 relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 h-1 w-full" style={{ background: c.accent }} />
                <div className="font-display font-semibold text-3xl sm:text-4xl md:text-5xl text-[#222629] tracking-tight leading-none tabular-nums">
                  <AnimatedCounter to={c.to} suffix={c.suffix} decimals={c.decimals} testId={c.testId} />
                </div>
                <div className="mt-3 md:mt-4 text-[10px] sm:text-[11px] uppercase tracking-[0.16em] sm:tracking-[0.18em] text-neutral-500 font-heading font-semibold">{c.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section data-testid="about-section" className="py-20 md:py-32 relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 grid lg:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="lg:col-span-5">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#2F9E44] mb-4">About R K Steel</div>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold text-[#222629] leading-[1.08] tracking-tight">
              A house built on <br className="hidden sm:block" /> integrity, alloyed with trust.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-neutral-700 leading-relaxed">
            <p>
              Conceived in 1955 through the vision of <strong className="text-[#12355B]">Late Shri K. Muthiah</strong>, R K STEEL began as a
              principled trader of industrial materials in South India. Every consignment, then and now, has been governed by a single
              covenant — deliver what we commit, in the quality we commit, on the day we commit.
            </p>
            <p>
              Under the leadership of <strong className="text-[#12355B]">Shri Ravichandran</strong>, the regional enterprise evolved into a
              globally respected industrial procurement and recycling organization — serving international markets while remaining rooted in
              trust, integrity and long-term relationships.
            </p>
            <div className="grid grid-cols-2 gap-6 pt-4">
              {[
                { icon: <Globe size={20} />, k: "Global Reach", v: "30+ countries" },
                { icon: <Award size={20} />, k: "Certified Trader", v: "GST · IEC · MSME · TAN" },
                { icon: <Factory size={20} />, k: "Portfolio", v: "Metals · Rubber · Machinery" },
                { icon: <Users size={20} />, k: "Team", v: "Family-led leadership" },
              ].map((x) => (
                <div key={x.k} className="border-t border-neutral-200 pt-4">
                  <div className="text-[#12355B] mb-2">{x.icon}</div>
                  <div className="font-heading text-sm text-[#222629] font-semibold">{x.k}</div>
                  <div className="text-xs text-neutral-500 mt-1">{x.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section data-testid="timeline-section" className="py-20 md:py-32 bg-[#F7F8F5]/50 relative">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="max-w-2xl mb-14 md:mb-20">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#2F9E44] mb-4">Our Journey</div>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold text-[#222629] tracking-tight leading-tight">
              From 1955 to today — seven decades of steel.
            </h2>
          </div>
          <Timeline />
        </div>
      </section>

      {/* GLOBAL OPS CTA */}
      <section data-testid="global-ops-cta" className="py-20 md:py-32 bg-[#222629] text-white relative overflow-hidden">
        <div className="grain absolute inset-0" />
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 grid lg:grid-cols-2 gap-10 md:gap-14 items-center relative">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#B8823E] mb-4">Global Operations</div>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-white leading-[1.08]">
              Anchored in India. <br />
              <span className="text-[#B8823E]">Connected to the world.</span>
            </h2>
            <p className="mt-5 md:mt-6 text-neutral-300 max-w-xl leading-relaxed">
              From our headquarters in Bengaluru, our procurement and logistics network extends across India while our international trade corridors connect the Middle East, Europe, the United States of America, Canada, Australia, Africa and Asia — delivering trusted industrial recycling solutions across global markets.
            </p>
            <Link to="/contact" data-testid="cta-partner" className="btn-pill btn-primary mt-7 md:mt-8">
              Partner with us <ArrowRight size={16} />
            </Link>
          </div>
          <div className="rounded-2xl overflow-hidden bg-white/5 border border-white/10">
            <img
              src="https://images.unsplash.com/photo-1724597500306-a4cbb7d1324e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxsb2dpc3RpY3MlMjBzaGlwcGluZyUyMGdsb2JhbHxlbnwwfHx8fDE3ODQxMjExMjd8MA&ixlib=rb-4.1.0&q=85"
              alt="Global logistics"
              className="w-full h-64 sm:h-80 md:h-[400px] object-cover"
            />
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Home;
